import React, { useRef, useState, useEffect } from 'react';
import { X, Play, Pause, Volume2, VolumeX, Maximize2, RotateCcw } from 'lucide-react';
import Button from './Button';
import { RecordedVideo } from '../hooks/useScreenCapture';

interface VideoPopupModalProps {
  isOpen: boolean;
  onClose: () => void;
  video: RecordedVideo;
}

const VideoPopupModal: React.FC<VideoPopupModalProps> = ({
  isOpen,
  onClose,
  video
}) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [isVideoLoaded, setIsVideoLoaded] = useState(false);
  const [videoUrl, setVideoUrl] = useState<string | null>(null);

  const formatTime = (seconds: number) => {
    if (!isFinite(seconds) || isNaN(seconds) || seconds < 0) {
      return '00:00';
    }
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const handlePlayPause = () => {
    const videoElement = videoRef.current;
    if (!videoElement) return;

    if (isPlaying) {
      videoElement.pause();
    } else {
      videoElement.play();
    }
    setIsPlaying(!isPlaying);
  };

  const handleMuteToggle = () => {
    if (videoRef.current) {
      videoRef.current.muted = !isMuted;
      setIsMuted(!isMuted);
    }
  };

  const handleSeek = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (videoRef.current) {
      const newTime = parseFloat(e.target.value);
      videoRef.current.currentTime = newTime;
      setCurrentTime(newTime);
    }
  };

  const handleFullscreen = () => {
    if (videoRef.current) {
      if (videoRef.current.requestFullscreen) {
        videoRef.current.requestFullscreen();
      }
    }
  };

  const handleRestart = () => {
    if (videoRef.current) {
      videoRef.current.currentTime = 0;
      setCurrentTime(0);
    }
  };

  useEffect(() => {
    if (isOpen && videoRef.current) {
      const videoElement = videoRef.current;
      const newVideoUrl = URL.createObjectURL(video.blob);
      setVideoUrl(newVideoUrl);
      
      // Reset state when modal opens
      setIsPlaying(false);
      setCurrentTime(0);
      setIsVideoLoaded(false);
      
      // Set initial duration
      if (video.duration && video.duration > 0) {
        setDuration(video.duration);
        setIsVideoLoaded(true);
      }

      let isMetadataLoaded = false;

      const handleLoadedMetadata = () => {
        if (!isMetadataLoaded) {
          console.log('Video metadata loaded');
          isMetadataLoaded = true;
          if (videoElement.duration && isFinite(videoElement.duration)) {
            setDuration(videoElement.duration);
          }
          setIsVideoLoaded(true);
        }
      };

      const handleTimeUpdate = () => {
        setCurrentTime(videoElement.currentTime);
      };

      const handlePlay = () => {
        console.log('Video started playing');
        setIsPlaying(true);
      };

      const handlePause = () => {
        console.log('Video paused');
        setIsPlaying(false);
      };

      const handleEnded = () => {
        console.log('Video ended');
        setIsPlaying(false);
        setCurrentTime(0);
      };

      const handleError = (e: Event) => {
        console.error('Video error:', e);
        setIsVideoLoaded(false);
        setIsPlaying(false);
      };

      // Add event listeners
      videoElement.addEventListener('loadedmetadata', handleLoadedMetadata);
      videoElement.addEventListener('timeupdate', handleTimeUpdate);
      videoElement.addEventListener('play', handlePlay);
      videoElement.addEventListener('pause', handlePause);
      videoElement.addEventListener('ended', handleEnded);
      videoElement.addEventListener('error', handleError);

      return () => {
        videoElement.removeEventListener('loadedmetadata', handleLoadedMetadata);
        videoElement.removeEventListener('timeupdate', handleTimeUpdate);
        videoElement.removeEventListener('play', handlePlay);
        videoElement.removeEventListener('pause', handlePause);
        videoElement.removeEventListener('ended', handleEnded);
        videoElement.removeEventListener('error', handleError);
        URL.revokeObjectURL(newVideoUrl);
        setVideoUrl(null);
      };
    }
  }, [isOpen, video.blob, video.duration]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black bg-opacity-75 transition-opacity" onClick={onClose} />
      
      {/* Modal */}
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="relative w-full max-w-4xl transform overflow-hidden rounded-lg bg-white shadow-xl transition-all">
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900">
              Video Preview
            </h3>
            <Button
              variant="ghost"
              size="sm"
              onClick={onClose}
              leftIcon={<X className="h-4 w-4" />}
            >
              Close
            </Button>
          </div>

          {/* Video Player */}
          <div className="relative bg-black">
            <video
              ref={videoRef}
              src={videoUrl || undefined}
              className="w-full h-96 object-contain"
              preload="metadata"
              playsInline
              controls={false}
              autoPlay={false}
              muted={isMuted}
              style={{ backgroundColor: '#000' }}
            />
            
            {/* Video Overlay Controls */}
            <div className="absolute inset-0 bg-black bg-opacity-0 hover:bg-opacity-30 transition-all duration-200 flex items-center justify-center group">
              {!isVideoLoaded ? (
                <div className="flex items-center space-x-2 text-white">
                  <div className="w-8 h-8 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  <span className="text-lg">Loading video...</span>
                </div>
              ) : (
                <Button
                  variant="ghost"
                  size="xl"
                  onClick={handlePlayPause}
                  className="opacity-0 group-hover:opacity-100 transition-opacity duration-200 bg-black bg-opacity-50 text-white hover:bg-opacity-70"
                >
                  {isPlaying ? <Pause className="h-8 w-8" /> : <Play className="h-8 w-8" />}
                </Button>
              )}
            </div>
          </div>

          {/* Video Controls */}
          <div className="p-4 space-y-4">
            {/* Progress Bar */}
            <div className="space-y-2">
              <input
                type="range"
                min="0"
                max={duration || 0}
                value={currentTime}
                onChange={handleSeek}
                disabled={!isVideoLoaded}
                className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer slider disabled:opacity-50 disabled:cursor-not-allowed"
              />
              <div className="flex justify-between text-sm text-gray-500">
                <span>{formatTime(currentTime)}</span>
                <span>{isVideoLoaded ? formatTime(duration) : 'Loading...'}</span>
              </div>
            </div>

            {/* Control Buttons */}
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handlePlayPause}
                  disabled={!isVideoLoaded}
                  leftIcon={isPlaying ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />}
                >
                  {!isVideoLoaded ? 'Loading...' : isPlaying ? 'Pause' : 'Play'}
                </Button>
                
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleMuteToggle}
                  leftIcon={isMuted ? <VolumeX className="h-4 w-4" /> : <Volume2 className="h-4 w-4" />}
                >
                  {isMuted ? 'Unmute' : 'Mute'}
                </Button>

                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleRestart}
                  leftIcon={<RotateCcw className="h-4 w-4" />}
                >
                  Restart
                </Button>
              </div>

              <div className="flex items-center space-x-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleFullscreen}
                  leftIcon={<Maximize2 className="h-4 w-4" />}
                >
                  Fullscreen
                </Button>
              </div>
            </div>

            {/* Video Info */}
            <div className="text-sm text-gray-500">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <span className="font-medium">Duration:</span> {isVideoLoaded ? formatTime(duration) : formatTime(video.duration)}
                </div>
                <div>
                  <span className="font-medium">Size:</span> {(video.blob.size / (1024 * 1024)).toFixed(1)} MB
                </div>
                <div>
                  <span className="font-medium">Type:</span> {video.blob.type}
                </div>
                <div>
                  <span className="font-medium">Status:</span> {isVideoLoaded ? 'Ready' : 'Loading...'}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VideoPopupModal;
